import {
  addDecisionsEvent,
  DecisionContext,
  DbotEventDispatcher,
} from "@/dbot/common/commonDomain";
import { makeAutoObservable } from "mobx";
import { newUuid } from "@/utils/uuid";
import { TMessage } from "@/i18n/tt";
import { DecisionType } from "@/common/decisions";
import { BooleanFieldModel, NumberFieldModel, StringFieldModel } from "@/common/commonForms";

export type ApproveNominationCommitteeRemuneration = {
  type: DecisionType.approveNominationCommitteeRemuneration;
  isReceive?: boolean;
  remunerationPeriods?: Array<RemunerationPeriod>;
  nominationCommitteeRemunerationByName?: Array<Remuneration>;
  nominationCommitteeRemuneration?: Array<Remuneration>;
};

export enum RemunerationPeriod {
  untilNextGeneralMeeting = "untilNextGeneralMeeting",
  fromLastGeneralMeetingToCurrent = "fromLastGeneralMeetingToCurrent",
}

export type Remuneration = {
  id: string;
  name: string;
  value: number;
};

export type RemunerationModel = {
  id: string;
  name: string;
  valueField: NumberFieldModel;
};

export class ApproveNominationCommitteeRemunerationForm {
  readonly type = DecisionType.approveNominationCommitteeRemuneration;
  readonly dispatch: DbotEventDispatcher;
  isReceive: BooleanFieldModel;
  remunerationPeriods: RemunerationPeriodsModel;
  newNominationCommittee: Array<RemunerationModel> = []; // this comes from the electNominationCommitteeMember decision
  nominationCommitteeRemuneration: Array<RemunerationModel> = [];
  isDisplayAddNewMemberDialog: boolean;
  newMemberName: StringFieldModel;
  remunerationPeriodValidationMessage?: TMessage;

  constructor(
    dispatch: DbotEventDispatcher,
    private context: DecisionContext<ApproveNominationCommitteeRemuneration>,
    public data?: ApproveNominationCommitteeRemuneration,
  ) {
    const isReceive = new BooleanFieldModel({ value: data?.isReceive, required: true });
    this.isReceive = isReceive;
    this.remunerationPeriods = new RemunerationPeriodsModel({
      value: data?.remunerationPeriods,
      get required() {
        return isReceive.value === true;
      },
    });
    this.isDisplayAddNewMemberDialog = false;
    this.newMemberName = new StringFieldModel({ required: true });
    this.dispatch = dispatch;
    makeAutoObservable(this, {}, { autoBind: true });
    data?.nominationCommitteeRemuneration?.forEach(this.addNewNominationCommittee);
    data?.nominationCommitteeRemunerationByName?.forEach(this.addNominationCommitteeByName);
  }

  init() {
    // Reset the form to the latest data when it is displayed.
    // (the server updates the data whenever the dependent ElectNominationCommittee decision is changed)
    this.newNominationCommittee = [];
    this.context.decisionData.nominationCommitteeRemuneration?.forEach(
      this.addNewNominationCommittee,
    );
  }

  get hasElectNominationCommitteeDecision() {
    return this.context.currentDecisions.some(
      (decision) => decision.type === DecisionType.electNominationCommittee,
    );
  }

  removeIfFound(committeeId: string) {
    const i = this.newNominationCommittee.findIndex((e) => e.id === committeeId);
    if (i > -1) {
      this.newNominationCommittee.splice(i, 1);
    }
  }

  get decisionIndex() {
    return this.context.currentDecisions.findIndex(
      (decision) => decision.type === DecisionType.approveNominationCommitteeRemuneration,
    );
  }

  addElectNominationCommitteeDecision() {
    const decisionType = DecisionType.electNominationCommittee;
    this.dispatch(addDecisionsEvent([{ decisionType }], { index: this.decisionIndex }));
  }

  get isProjectLocked(): boolean {
    return this.context.isLocked;
  }

  get isReceiveAndRemunerationPeriodValid(): boolean {
    if (!this.isReceive.isValid) {
      return false;
    }
    if (this.isReceive.value === false) {
      return true;
    }
    if (this.isReceive.value === true && !this.remunerationPeriods.isValid) {
      return false;
    }
    if (
      this.remunerationPeriods.value.includes(RemunerationPeriod.untilNextGeneralMeeting) &&
      (this.newNominationCommittee.length === 0 ||
        this.newNominationCommittee.some((n) => !n.valueField.isValid))
    ) {
      return false;
    }
    if (
      this.remunerationPeriods.value.includes(RemunerationPeriod.fromLastGeneralMeetingToCurrent) &&
      (this.nominationCommitteeRemuneration.length === 0 ||
        this.nominationCommitteeRemuneration.some((n) => !n.valueField.isValid))
    ) {
      return false;
    }
    return this.remunerationPeriodValidationMessage === undefined;
  }

  get isValid() {
    return this.isReceiveAndRemunerationPeriodValid;
  }

  validate(): boolean {
    this.remunerationPeriodValidationMessage = undefined;
    this.isReceive.validate();
    this.remunerationPeriods.validate();
    if (this.remunerationPeriods.value.includes(RemunerationPeriod.untilNextGeneralMeeting)) {
      this.newNominationCommittee.forEach((m) => m.valueField.validate());
    }
    if (
      this.remunerationPeriods.value.includes(RemunerationPeriod.fromLastGeneralMeetingToCurrent)
    ) {
      this.nominationCommitteeRemuneration.forEach((m) => m.valueField.validate());
    }
    if (this.isReceive.value === true && !this.remunerationPeriods.isValid) {
      this.remunerationPeriodValidationMessage = {
        key: "decisions.approveNominationCommitteeRemuneration.warning",
      };
    }
    return this.isValid;
  }

  addNewNominationCommittee({ id, name, value }: Remuneration) {
    if (!this.newNominationCommittee.some((d) => d.id === id))
      this.newNominationCommittee.push({
        id,
        name,
        valueField: new NumberFieldModel({ value, required: true, isPositive: true }),
      });
  }

  addNominationCommitteeByName({ id, name, value }: Remuneration) {
    this.nominationCommitteeRemuneration.push({
      id,
      name,
      valueField: new NumberFieldModel({ value, required: true, isPositive: true }),
    });
  }

  removeNominationCommitteeByName(id: string) {
    const index = this.nominationCommitteeRemuneration.findIndex((n) => n.id === id);
    if (index >= 0) {
      this.nominationCommitteeRemuneration.splice(index, 1);
    }
  }

  displayAddNewMemberDialog() {
    this.isDisplayAddNewMemberDialog = true;
  }

  hideAddNewMemberDialog() {
    this.isDisplayAddNewMemberDialog = false;
    this.newMemberName.clear();
  }

  addNewMemberAndCloseDialog() {
    this.newMemberName.commit();
    this.newMemberName.validate();
    if (this.newMemberName.isValid) {
      const newId = newUuid();
      const newMember = {
        id: newId,
        name: this.newMemberName.validatedValue,
        value: 0,
      };
      this.addNominationCommitteeByName(newMember);
      this.newMemberName.setInputValue("");
      this.hideAddNewMemberDialog();
      return newId;
    }
  }
}

export class RemunerationPeriodsModel {
  readonly params: { value?: Array<RemunerationPeriod>; required?: boolean };

  value: Array<RemunerationPeriod>;
  error?: TMessage = undefined;

  constructor(params: { value?: Array<RemunerationPeriod>; required?: boolean } = {}) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.params = params;
    this.value = params.value ?? [];
  }

  get isRequired(): boolean {
    return Boolean(this.params.required);
  }

  get isValid(): boolean {
    return !this.error;
  }

  validate() {
    if (this.params.required && this.value.length === 0) {
      this.error = { key: "required" };
    } else {
      this.error = undefined;
    }
  }

  setValue(value: Array<RemunerationPeriod>) {
    this.value = value;
  }

  add(period: RemunerationPeriod) {
    this.value.push(period);
  }

  remove(id: RemunerationPeriod) {
    for (let i = 0; i < this.value.length; i++) {
      if (this.value[i] === id) {
        this.value.splice(i, 1);
      }
    }
  }

  isSelected(id: RemunerationPeriod) {
    return this.value.includes(id);
  }

  toggle(id: RemunerationPeriod) {
    if (this.isSelected(id)) {
      this.remove(id);
    } else {
      this.add(id);
    }
  }
}
